import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowUpRight, RotateCcw } from "lucide-react";


/**
 * Vergleichsansicht der Aufklapp-Abschnitte (Ticket VG1).
 *
 * Zweck: Abstände und Zeilenhöhen der Aufklapp-Liste auf den typischen
 * Breiten Handy / Tablet / Schreibtisch nebeneinander prüfen. Die Vorschauen
 * laufen in eigenen Rahmen (iframe) auf derselben Herkunft, damit die
 * Umbruchpunkte von Tailwind echt greifen — ein schmaler Container allein
 * würde weiterhin die Desktop-Stufen zeigen.
 *
 * Reines Prüfwerkzeug: keine Daten, keine Protokollspur, keine Änderung an
 * der geprüften Seite.
 */
export const Route = createFileRoute("/_admin/admin/abschnitte-vorschau")({
  component: AbschnitteVorschauPage,
  head: () => ({
    meta: [
      { title: "Abschnitte im Breitenvergleich – creategy" },
      {
        name: "description",
        content:
          "Internes Prüfwerkzeug: Aufklapp-Abschnitte auf Handy-, Tablet- und Desktop-Breite nebeneinander vergleichen.",
      },
      { property: "og:title", content: "Abschnitte im Breitenvergleich – creategy" },
      {
        property: "og:description",
        content:
          "Abstände und Zeilenhöhen der Aufklapp-Abschnitte auf typischen Breiten prüfen.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

type Viewport = {
  id: string;
  label: string;
  hint: string;
  width: number;
  height: number;
};

const VIEWPORTS: Viewport[] = [
  { id: "mobile", label: "Handy", hint: "390 × 844 (iPhone-Klasse)", width: 390, height: 844 },
  { id: "tablet", label: "Tablet", hint: "768 × 1024 (iPad hochkant)", width: 768, height: 1024 },
  { id: "desktop", label: "Schreibtisch", hint: "1440 × 900 (Laptop)", width: 1440, height: 900 },
];

const PAGES = [
  { path: "/wahlkalender/baden-wuerttemberg", label: "Wahlkalender Baden-Württemberg" },
  { path: "/wahlkalender/bayern", label: "Wahlkalender Bayern" },
  { path: "/wahlkalender/nordrhein-westfalen", label: "Wahlkalender Nordrhein-Westfalen" },
];

const ANCHOR = "#wahlkalender-abschnitte";

/**
 * Prüfzustände (Ergänzung 22.09.2026): Die Aufklapp-Liste lässt genau einen
 * Abschnitt offen. Damit Touch-, Fokus- und Zeilenhöhen-Unterschiede je Breite
 * vergleichbar sind, stellt die Vergleichsansicht denselben Zustand in allen
 * drei Vorschauen her — über echte Klicks bzw. echten Fokus im Rahmen, nicht
 * über eigene Stile. So bleibt die geprüfte Seite unverändert.
 */
type PruefZustand =
  | "wie-geladen"
  | "erster-offen"
  | "letzter-offen"
  | "alle-geschlossen"
  | "fokus-erster";

const PRUEFZUSTAENDE: { id: PruefZustand; label: string; hint: string }[] = [
  {
    id: "wie-geladen",
    label: "Wie geladen",
    hint: "Ausgangszustand der Seite: der erste Abschnitt ist offen.",
  },
  {
    id: "erster-offen",
    label: "Erster Abschnitt geöffnet",
    hint: "Geöffneter Zustand oben: Abstand Überschrift zu Text und Zeilenhöhe im Fließtext.",
  },
  {
    id: "letzter-offen",
    label: "Letzter Abschnitt geöffnet",
    hint: "Geöffneter Zustand unten: Abstand nach dem letzten Abschnitt und Trennlinien darüber.",
  },
  {
    id: "alle-geschlossen",
    label: "Alle geschlossen",
    hint: "Geschlossener Zustand: Zeilenabstände der Überschriften, Trennlinien und Tippfläche.",
  },
  {
    id: "fokus-erster",
    label: "Tastaturfokus auf erster Überschrift",
    hint: "Fokusring in Marken-Gelb prüfen; danach bewegen Pfeiltasten den Fokus im Rahmen.",
  },
];


function AbschnitteVorschauPage() {
  const [path, setPath] = useState(PAGES[0]!.path);
  const [scale, setScale] = useState(45);
  const [jumpToSections, setJumpToSections] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [zustand, setZustand] = useState<PruefZustand>("wie-geladen");
  const frameRefs = useRef<Record<string, HTMLIFrameElement | null>>({});

  const src = useMemo(
    () => `${path}${jumpToSections ? ANCHOR : ""}`,
    [path, jumpToSections],
  );

  /** Stellt den gewählten Zustand in einem Rahmen über echte Bedienung her. */
  const applyZustand = useCallback(
    (frame: HTMLIFrameElement | null, gewuenscht: PruefZustand) => {
      if (!frame || gewuenscht === "wie-geladen") return;
      const doc = frame.contentDocument;
      if (!doc) return;
      const triggers = Array.from(
        doc.querySelectorAll<HTMLButtonElement>(
          "#wahlkalender-abschnitte button[aria-expanded]",
        ),
      );
      if (triggers.length === 0) return;

      const offene = triggers.filter(
        (trigger) => trigger.getAttribute("aria-expanded") === "true",
      );

      if (gewuenscht === "alle-geschlossen") {
        offene.forEach((trigger) => trigger.click());
        return;
      }

      if (gewuenscht === "fokus-erster") {
        triggers[0]!.focus();
        return;
      }

      const ziel =
        gewuenscht === "letzter-offen" ? triggers[triggers.length - 1]! : triggers[0]!;
      if (ziel.getAttribute("aria-expanded") !== "true") ziel.click();
      offene
        .filter((trigger) => trigger !== ziel)
        .forEach((trigger) => trigger.click());
      ziel.scrollIntoView({ block: "center" });
    },
    [],
  );

  // Zustandswechsel gilt für alle drei Breiten gleichzeitig, damit der
  // Vergleich immer denselben Zustand zeigt.
  useEffect(() => {
    VIEWPORTS.forEach((viewport) => {
      applyZustand(frameRefs.current[viewport.id] ?? null, zustand);
    });
  }, [applyZustand, zustand, src, reloadKey]);


  return (
    <div className="space-y-8">
      <header className="space-y-3">
        <h1 className="heading-h2 text-primary">Abschnitte im Breitenvergleich</h1>
        <p className="max-w-[820px] font-body text-base leading-relaxed text-muted-foreground">
          Die Aufklapp-Abschnitte in drei Breiten nebeneinander. Jede Vorschau ist die echte
          Seite in einem eigenen Rahmen, deshalb greifen die Stufen für Abstände und
          Zeilenhöhen genau so wie auf dem jeweiligen Gerät. Klicken und Überfahren
          funktioniert in den Vorschauen wie gewohnt.
        </p>
      </header>

      <div className="grid gap-4 rounded-[4px] border border-border bg-card p-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid gap-1.5">
            <span className="font-body text-sm text-muted-foreground">Seite</span>
            <select
              value={path}
              onChange={(event) => setPath(event.target.value)}
              className="h-10 rounded-[4px] border border-input bg-background px-3 font-body text-sm text-foreground"
            >
              {PAGES.map((page) => (
                <option key={page.path} value={page.path}>
                  {page.label}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-1.5">
            <span className="font-body text-sm text-muted-foreground">
              Darstellungsgröße: {scale}%
            </span>
            <input
              type="range"
              min={25}
              max={100}
              step={5}
              value={scale}
              onChange={(event) => setScale(Number(event.target.value))}
              className="h-10 accent-[color:var(--brand-petrol)]"
            />
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 font-body text-sm text-foreground">
            <input
              type="checkbox"
              checked={jumpToSections}
              onChange={(event) => setJumpToSections(event.target.checked)}
              className="h-4 w-4"
            />
            Direkt zu den Abschnitten springen
          </label>
          <button
            type="button"
            onClick={() => setReloadKey((value) => value + 1)}
            className="inline-flex h-10 items-center gap-2 rounded-[4px] bg-brand-button px-4 font-display text-sm text-brand-button-foreground"
          >
            <RotateCcw aria-hidden="true" className="h-4 w-4" />
            Vorschauen neu laden
          </button>
        </div>
      </div>

      {/* Prüfzustände: gelten gleichzeitig für alle drei Breiten. */}
      <div className="space-y-3 rounded-[4px] border border-border bg-card p-4">
        <div className="flex flex-wrap gap-2">
          {PRUEFZUSTAENDE.map((eintrag) => {
            const aktiv = eintrag.id === zustand;
            return (
              <button
                key={eintrag.id}
                type="button"
                aria-pressed={aktiv}
                onClick={() => setZustand(eintrag.id)}
                className={`inline-flex h-10 items-center rounded-[4px] px-4 font-display text-sm transition-colors ${
                  aktiv
                    ? "bg-brand-button text-brand-button-foreground"
                    : "border border-input bg-background text-foreground hover:bg-muted"
                }`}
              >
                {eintrag.label}
              </button>
            );
          })}
        </div>
        <p className="font-body text-sm leading-relaxed text-muted-foreground">
          {PRUEFZUSTAENDE.find((eintrag) => eintrag.id === zustand)?.hint}
        </p>
      </div>


      <div className="flex flex-wrap items-start gap-8">
        {VIEWPORTS.map((viewport) => {
          const factor = scale / 100;
          return (
            <section key={viewport.id} className="space-y-3">
              <div className="flex items-baseline gap-3">
                <h2 className="heading-h3 text-primary">{viewport.label}</h2>
                <span className="font-body text-sm text-muted-foreground">{viewport.hint}</span>
                <a
                  href={src}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-display text-sm text-primary underline"
                >
                  Einzeln öffnen
                  <ArrowUpRight aria-hidden="true" className="h-3.5 w-3.5" />
                </a>
              </div>

              <div
                className="overflow-hidden rounded-[4px] border border-border bg-background"
                style={{
                  width: viewport.width * factor,
                  height: viewport.height * factor,
                }}
              >
                <iframe
                  key={`${viewport.id}-${reloadKey}-${src}`}
                  ref={(node) => {
                    frameRefs.current[viewport.id] = node;
                  }}
                  onLoad={(event) => applyZustand(event.currentTarget, zustand)}
                  title={`Vorschau ${viewport.label} (${viewport.width} px)`}
                  src={src}
                  width={viewport.width}
                  height={viewport.height}
                  style={{
                    transform: `scale(${factor})`,
                    transformOrigin: "top left",
                    border: "0",
                  }}
                />

              </div>
            </section>
          );
        })}
      </div>

      <p className="max-w-[820px] font-body text-sm leading-relaxed text-muted-foreground">
        Hinweis: Die Darstellungsgröße verkleinert nur die Anzeige, nicht die Seite selbst —
        gemessene Abstände bleiben die des angegebenen Geräts. Für eine 1:1-Ansicht stellen Sie
        100 % ein oder öffnen die Breite einzeln.
      </p>
    </div>
  );
}
